import { Page } from "../enums/Page";
import { GameEventService } from "../services/GameEventService";
import { InventoryService } from "../services/InventoryService";
import { html, css } from "../helpers/webComponents";

export class InventoryComponent extends HTMLElement {
    private readonly _gameEventService: GameEventService = new GameEventService();
    private readonly _inventoryService: InventoryService = new InventoryService();
    private _inventoryItems: string[] = [];

    // Total number of possible items to collect
    private readonly TOTAL_COLLECTIBLE_ITEMS: number = 9;

    public async connectedCallback(): Promise<void> {
        this.attachShadow({ mode: "open" });
        await this.loadInventory();
        this.render();
    }

    // Load the player's inventory, awaits the ImportService to fetch the inventory data
    private async loadInventory(): Promise<void> {
        try {
            const result: { inventory: string[] } = await this._inventoryService.openInventory();
            this._inventoryItems = result.inventory;
        }
        catch {
            this._inventoryItems = [];
        }
    }

    // Calculate the progress percentage based on the number of items collected
    private calculateProgressPercentage(): number {
        const collectedItemsCount: number = this._inventoryItems.length;
        return (collectedItemsCount / this.TOTAL_COLLECTIBLE_ITEMS) * 100;
    }

    // Render the inventory component as HTML element
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const progressPercentage: number = this.calculateProgressPercentage();

        const element: HTMLElement = html`
            <div>
                <style>${styles}</style>
                <div class="inventory-header">
                    <h2>Inventory</h2>
                    <div class="progress-container">
                        <div class="progress-label">
                            Items Collected: ${this._inventoryItems.length} / ${this.TOTAL_COLLECTIBLE_ITEMS}
                        </div>
                        <div class="progress-bar">
                            <div 
                                class="progress-bar-fill" 
                                style="width: ${progressPercentage}%"
                            ></div>
                        </div>
                    </div>
                </div>
                <div class="inventory-list">
                    ${this._inventoryItems.length > 0
                        ? this._inventoryItems.map(item => `
                            <div class="inventory-item">${item}</div>
                        `).join("")
                        : "<div class=\"inventory-item\">No items in inventory</div>"}
                </div>
                <div class="navigation">
                    <button id="switch-to-canvas">Close</button>
                </div>
            </div>
        `;

        // Add event listener for page switching
        element.querySelector("#switch-to-canvas")?.addEventListener("click", () => {
            this._gameEventService.switchPage(Page.Canvas);
        });

        // Clear any existing content and append the new element
        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }
        this.shadowRoot.append(element);
    }
}

const styles: string = css`
:host {
    display: block;
    width: 100%;
    height: 100vh;
    margin: 0;
    padding: 20px;
    font-family: "Onesize";
    color: #bb0000;
    background-color: #0a0a0a;
    background-blend-mode: multiply;
    line-height: 1.6;
    max-width: none;
    box-sizing: border-box;
    text-shadow: 0 0 5px rgba(255, 0, 0, 0.3);
}

.inventory-header h2 {
    text-align: center;
    margin-bottom: 20px;
    font-size: 1.8em;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: #ffffff;
    text-shadow: 0 0 8px rgba(255, 0, 0, 0.5);
}

.progress-label {
    text-align: center;
    margin-bottom: 5px;
    font-size: 0.9em;
    color: #ffffff;
}

.progress-bar {
    width: 80%;
    height: 20px;
    margin: 0 auto;
    background-color: #1a0000;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid #4d0000;
}

.progress-bar-fill {
    height: 100%;
    background-color: #800000;
    transition: width 0.5s ease-in-out;
    box-shadow: 0 0 10px #ff0000 inset;
}

.inventory-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: center;
    margin-top: 20px;
}

.inventory-item {
    background-color: #1a0000;
    border: 2px solid #4d0000;
    padding: 10px;
    border-radius: 5px;
    width: 180px;
    text-align: center;
    color: #ffffff;
    box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.8), 0 0 5px rgba(255, 0, 0, 0.2) inset;
    transition: all 0.3s ease;
}

.inventory-item:hover {
    background-color: #340000;
    cursor: pointer;
    transform: scale(1.05);
    box-shadow: 3px 3px 8px rgba(0, 0, 0, 0.9), 0 0 8px rgba(255, 0, 0, 0.4) inset;
}

.navigation {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    width: max-content;
}


.navigation button {
    background-color: #340000;
    color: #ffffff;
    border: 2px solid #670000;
    padding: 12px 30px;
    font-size: 16px;
    font-family: "Onesize";
    text-align: center;
    text-decoration: none;
    display: inline-block;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.3s, transform 0.2s;
    letter-spacing: 1px;
    text-transform: uppercase;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
}

.navigation button:hover {
    background-color: #670000;
    color: #ffffff;
    transform: scale(1.05);
    text-shadow: 0 0 5px rgba(255, 0, 0, 0.7);
}

.navigation button:active {
    transform: scale(0.98);
    background-color: #8a0000;
}
`;
